/**
 * Main types index - exports all domain-specific types
 * Central location for all application types
 */

// Re-export from specialized type files
export * from './api';
export * from './ui';
export * from './glAccount';
export * from './transactionImport';
export * from './transactionRule';
export * from './transactionSplit';
export * from './plaid';
export * from './organization';

// Core business domain types

// Currency type
export interface Currency {
  code: string;
  symbol: string;
  name: string;
  exchangeRate: number;
}

// Account types and enums
export type AccountType = 'checking' | 'savings' | 'investment' | 'credit';

// Frontend Account interface (used by components)
export interface Account {
  id: string;
  name: string;
  type: AccountType;
  balance: number;        // External API balance (Plaid/Mono)
  runningTotal: number;   // Calculated balance from transactions  
  transactionCount: number; // Count of transactions for this account
  currency: string;
  institution: string;
  isActive: boolean;      // Account status - active/inactive
  lastUpdated: Date;
}

// Backend Account interface (internal use for API communication)
export interface BackendAccount {
  id: string;
  organizationId: string;
  name: string;
  type: 'CHECKING' | 'SAVINGS' | 'INVESTMENT' | 'CREDIT';
  institution: string;
  balance: number;        // External API balance (Plaid/Mono)
  runningTotal: number;   // Calculated balance from transactions
  transactionCount: number; // Count of transactions for this account
  currency: string;
  isActive: boolean;
  plaidAccountId?: string;
  monoAccountId?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  lastUpdated: Date | string;
}

// Account with dual currency display support
export interface AccountWithCurrencyDisplay {
  id: string;
  name: string;
  type: AccountType;
  institution: string;
  // Original amounts in account's native currency
  balance: number;
  runningTotal: number;
  currency: string;
  // Converted amounts in selected display currency
  convertedBalance: number;
  convertedRunningTotal: number;
  convertedCurrency: string;
  // Metadata
  transactionCount: number;
  isActive: boolean;      // Account status - active/inactive
  lastUpdated: Date;
}

// Aggregated account balances response with pre-calculated totals
export interface AccountBalancesAggregatedResponse {
  // Converted totals in target currency
  totalRunningBalance: number;
  totalExternalBalance: number;
  targetCurrency: string;
  
  // Original totals grouped by currency
  originalRunningTotalsByCurrency: { [currency: string]: number };
  originalBalancesByCurrency: { [currency: string]: number };
  
  // Individual account details with dual currency display
  accounts: AccountWithCurrencyDisplay[];
}

// Account operation input types
export interface CreateAccountInput {
  name: string;
  type: AccountType;
  institution: string;
  balance: number;
  currency: string;
  plaidAccountId?: string;
  monoAccountId?: string;
}

export interface UpdateAccountInput {
  name?: string;
  type?: AccountType;
  institution?: string;
  balance?: number;
  currency?: string;
  isActive?: boolean;
  plaidAccountId?: string;
  monoAccountId?: string;
}

export interface AccountFilters {
  type?: AccountType;
  currency?: string;
  isActive?: boolean;
  institution?: string;
}

// Transaction status types
export type ReconciliationStatusType = 'reconciled' | 'unreconciled';
export type TransactionStatus = 'UNDER_REVIEW' | 'APPROVED' | 'DECLINED' | 'SPLIT';

// Transfer status types
export type TransferStatus = 'NO_MATCH' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

// Transaction type
export interface Transaction {
  id: string;
  organizationId?: string;
  
  // User-supplied transaction ID for idempotency (from bank statements)
  userTransactionId: string;
  
  // Core transaction fields
  timestamp: Date | string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  reconciled: boolean;
  currency: string; // ISO 4217 currency code
  
  // Account relationship
  accountId: string;
  accountName: string;
  accountInstitution?: string; // Institution name from account data
  account?: Account; // Optional for backward compatibility
  
  // GL Account assignment
  glAccountNumber: string;
  glAccountName: string;
  glSubcategory?: string;
  category: string;
  
  // Rule-based assignment tracking
  ruleBasedAssignment?: boolean;
  appliedRuleId?: string;
  appliedRuleName?: string;
  appliedRuleDetails?: {
    id?: string;
    description: string;
    naturalLanguageRule: string;
  } | null;
  
  // AI prediction metadata
  aiPredictionConfidence?: number;
  aiPredictionUsed?: boolean;
  
  // Transaction status management
  status?: TransactionStatus;
  reviewedAt?: Date | string;
  reviewedBy?: string;
  
  // Transfer tracking fields
  isTransfer?: boolean;
  transferId?: string;
  transferPartnerTransactionId?: string;
  transferStatus?: string; // NO_MATCH, UNDER_REVIEW, APPROVED, REJECTED
  transferFeeAmount?: number;
  transferFeeCurrency?: string;
  transferDetectedAt?: Date | string;
  transferReviewedAt?: Date | string;
  transferReviewedBy?: string;
  
  // Split transaction fields
  parentTransactionId?: string;
  splitIndex?: number;
  isSplitChild?: boolean;
  
  // Status tracking (for backward compatibility)
  statuses?: {
    reconciled: boolean;
    glAccountConfirmed: boolean;
  };
  
  // Source tracking metadata
  metadata?: {
    importedVia?: 'csv' | 'plaid' | 'manual';
    plaidTransactionId?: string;
    plaidAccountId?: string;
    csvImportBatch?: string;
    [key: string]: any;
  };
  
  // Audit fields
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Transaction type enum (matching backend)
export enum TransactionType {
  INCOME = 'INCOME',
  EXPENSE = 'EXPENSE'
}

// Transaction status management types
export interface TransactionStatusUpdateData {
  id: string;
  status: TransactionStatus;
  reviewedBy?: string;
  reviewedAt?: Date | string;
}

export interface TransactionStatusBulkUpdateData {
  transactionIds: string[];
  status: TransactionStatus;
  reviewedBy?: string;
  reviewedAt?: Date | string;
}

export interface TransactionStatusCounts {
  underReview: number;
  approved: number;
  declined: number;
  total: number;
}


// User type
export interface User {
  id: string;
  name: string;
  email: string;
  preferredCurrency: string;
}

// Filter types

// Frontend filter interface - used for UI state management
// These filters are converted to backend queries via TransactionService
export interface DateRangeFilter {
  startDate: Date | null;
  endDate: Date | null;
}

export interface TransactionFilters {
  dateRange: DateRangeFilter;
  categories: string[]; // Legacy - now handled by backend GL account filtering
  accounts: string[]; // Multiple account IDs - converted to backend queries
  types: ('income' | 'expense')[]; // Multiple types - converted to backend queries
  minAmount: number | null;
  maxAmount: number | null;
  searchTerm: string; // Converted to backend search parameter
  reconciled: boolean | null;
  status?: TransactionStatus | null;
  reviewedBy?: string | null;
}

// Note: Backend filtering is handled via TransactionService.TransactionFilters
// Frontend filters are automatically converted to optimized backend queries

// Financial analytics types
export interface FinancialRunway {
  months: number;
  burnRate: number;
  totalSavings: number;
  currentBalance: number;
  isProfitable: boolean;
  tooltipText: string;
  loading?: boolean;
  error?: string | null;
}

export interface ReconciliationStatus {
  total: number;
  reconciled: number;
  unreconciled: number;
  lastReconciled: Date;
}


// Transaction status utility functions
export const TransactionStatusUtils = {
  /**
   * Get display label for transaction status
   */
  getStatusLabel: (status: TransactionStatus): string => {
    switch (status) {
      case 'UNDER_REVIEW':
        return 'Under Review';
      case 'APPROVED':
        return 'Approved';
      case 'DECLINED':
        return 'Declined';
      case 'SPLIT':
        return 'Split';
      default:
        return 'Unknown';
    }
  },

  /**
   * Get CSS class for transaction status
   */
  getStatusClass: (status: TransactionStatus): string => {
    switch (status) {
      case 'UNDER_REVIEW':
        return 'status-under-review';
      case 'APPROVED':
        return 'status-approved';
      case 'DECLINED':
        return 'status-declined';
      case 'SPLIT':
        return 'status-split';
      default:
        return 'status-unknown';
    }
  },

  /**
   * Check if status can be changed
   */
  canChangeStatus: (currentStatus: TransactionStatus, targetStatus: TransactionStatus): boolean => {
    // All statuses can be changed to any other status
    return currentStatus !== targetStatus;
  },

  /**
   * Get all available statuses
   */
  getAllStatuses: (): TransactionStatus[] => {
    return ['UNDER_REVIEW', 'APPROVED', 'DECLINED', 'SPLIT'];
  },

  /**
   * Check if status requires review metadata
   */
  requiresReviewMetadata: (status: TransactionStatus): boolean => {
    return status === 'APPROVED' || status === 'DECLINED';
  }
} as const;

// Transfer status utility functions
export const TransferStatusUtils = {
  /**
   * Get display label for transfer status
   */
  getStatusLabel: (status: TransferStatus): string => {
    switch (status) {
      case 'NO_MATCH':
        return 'No Match';
      case 'UNDER_REVIEW':
        return 'Under Review';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return 'Unknown';
    }
  },

  /**
   * Get CSS class for transfer status
   */
  getStatusClass: (status: TransferStatus): string => {
    switch (status) {
      case 'NO_MATCH':
        return 'transfer-status-no-match';
      case 'UNDER_REVIEW':
        return 'transfer-status-under-review';
      case 'APPROVED':
        return 'transfer-status-approved';
      case 'REJECTED':
        return 'transfer-status-rejected';
      default:
        return 'transfer-status-unknown';
    }
  },

  /**
   * Check if transfer status can be changed
   */
  canChangeStatus: (currentStatus: TransferStatus, targetStatus: TransferStatus): boolean => {
    // Can transition from UNDER_REVIEW to APPROVED/REJECTED
    // Can transition from REJECTED back to NO_MATCH (for re-detection)
    if (currentStatus === 'UNDER_REVIEW' && (targetStatus === 'APPROVED' || targetStatus === 'REJECTED')) {
      return true;
    }
    if (currentStatus === 'REJECTED' && targetStatus === 'NO_MATCH') {
      return true;
    }
    return false;
  },

  /**
   * Get all available transfer statuses
   */
  getAllStatuses: (): TransferStatus[] => {
    return ['NO_MATCH', 'UNDER_REVIEW', 'APPROVED', 'REJECTED'];
  }
} as const;

// Transfer pair interface (for displaying matched transactions)
export interface TransferPair {
  id: string;
  organizationId: string;
  expenseTransactionId: string;
  incomeTransactionId: string;
  feeAmount: number;
  feeCurrency: string;
  status: TransferStatus;
  detectedAt: Date | string;
  reviewedAt?: Date | string;
  reviewedBy?: string;
  
  // Populated transaction data for display
  expenseTransaction?: Transaction;
  incomeTransaction?: Transaction;
}

// Transfer statistics interface
export interface TransferStats {
  noMatch: number;
  underReview: number;
  approved: number;
  rejected: number;
  totalFeeAmount: number;
  totalFeeCurrency: string;
  averageFeeAmount: number;
  dateRange: {
    startDate: Date | string;
    endDate: Date | string;
  };
}

// API request/response interfaces for transfer operations
export interface DetectTransfersResponse {
  success: boolean;
  transfersDetected: number;
  transfers: TransferPair[];
  error?: string;
}

export interface ApproveTransferRequest {
  transferId: string;
}

export interface ApproveTransferResponse {
  success: boolean;
  transfer: TransferPair;
}

export interface RejectTransferRequest {
  transferId: string;
  reason?: string;
}

export interface RejectTransferResponse {
  success: boolean;
  transfer: TransferPair;
}

export interface GetTransferPairsRequest {
  status?: TransferStatus;
  limit?: number;
  offset?: number;
}

export interface GetTransferPairsResponse {
  success: boolean;
  transfers: TransferPair[];
  totalCount: number;
}

export interface GetTransferStatsResponse {
  success: boolean;
  stats: TransferStats;
}

// Manual transfer creation interfaces
export interface CreateManualTransferRequest {
  expenseTransactionId: string;
  incomeTransactionId: string;
}

export interface CreateManualTransferResponse {
  success: boolean;
  transfer?: TransferPair;
  error?: string;
}

// Enriched transfer interfaces (matching backend structure)
export interface TransactionAccountInfo {
  id: string;
  institution: string;
  name: string;
}

export interface TransferTransactionData {
  timestamp: Date | string;
  description: string;
  amount: number;
  currency: string;
  account: TransactionAccountInfo;
}

export interface TransferConversionData {
  isCrossCurrency: boolean;
  exchangeRate: number;
  expenseAmountInIncomeCurrency: number;
}

export interface ExchangeFeeData {
  amount: number;
  currency: string;
}

export interface EnrichedTransferPair {
  id: string;
  status: TransferStatus;
  expenseTransaction: TransferTransactionData;
  incomeTransaction: TransferTransactionData;
  conversionData: TransferConversionData;
  exchangeFee: ExchangeFeeData;
}

export interface GetEnrichedTransferPairsRequest {
  status?: TransferStatus;
  limit?: number;
  offset?: number;
  preferredCurrency?: string;
}

export interface GetEnrichedTransferPairsResponse {
  underReview: number;
  approved: number;
  rejected: number;
  totalFees: number;
  totalFeesCurrency: string;
  transfers: EnrichedTransferPair[];
}
